import { useLogError } from '@/src/common/hooks/useLogError'
import type {
  GetPostedActivitiesQuery,
  GetPendingActivitiesQuery,
  CardActivityFeedCursorInput,
} from '@api/mesa/graphql'
import { useCallback } from 'react'
import { randomUUID } from '@/src/common/utils/randomUUID'

export type CardActivity =
  | GetPendingActivitiesQuery['cardActivityFeed']['page'][number]
  | GetPostedActivitiesQuery['cardActivityFeed']['page'][number]

export type TransactionEvent = Extract<CardActivity, { __typename: 'CardActivityTransactionEvent' }>

export type CardPayment = Extract<CardActivity, { __typename: 'CardActivityCardPayment' }>

export type StatementCredit = Extract<CardActivity, { __typename: 'CardActivityStatementCredit' }>

export type CardActivityId =
  | TransactionEvent['transactionEventId']
  | CardPayment['achTransferId']
  | StatementCredit['statementCreditId']

/**
 * the reason we use the union member's id instead of the `CardActivity.id` is that the `CardActivity.id` is ephemeral with [the current backend implementation](https://github.com/Acre-Rewards/acre-rewards-api/blob/feec71e6974d7ac9b8d71e651af89e61739138fb/src/credit-card/transactions/renderTransactionToCardActivityFeed/index.ts#L38-L54).
 * that is, it can change from render to render (a re-render occurs every time a new transaction event is processed). if we used the `CardActivity.id` for something
 * like the `key` prop in a react list, it would cause unnecessary re-renders in the UI because React wouldn't know that they are supposed to be the same component instance.
 *
 * [there's a ticket to implement an optimization such that `CardActivity.id` is permanent](https://linear.app/mesarewards/issue/MESA-854/use-set-methods-on-transactioneventid-to-determine-exactly-which-db)
 */
export function useGetCardActivityId() {
  const logError = useLogError()
  return useCallback(
    (cardActivity: CardActivity) => {
      switch (cardActivity.__typename) {
        case 'CardActivityTransactionEvent':
          return cardActivity.transactionEventId
        case 'CardActivityCardPayment':
          return cardActivity.achTransferId
        case 'CardActivityStatementCredit':
          return cardActivity.statementCreditId
        case 'CardActivityFinancialEvent':
          return cardActivity.financialEventId
        default:
          // if this check fails, then we haven't checked all `__typename`s
          true satisfies NonNullable<typeof cardActivity> extends never ? true : false
          logError('Unknown card activity type for useGetCardActivityId', cardActivity)
          return randomUUID()
      }
    },
    [logError],
  )
}

/** @see {@link useGetCardActivityId} */
export function useGetCardActivityCursor() {
  const logError = useLogError()
  return useCallback(
    (cardActivity: CardActivity) => {
      switch (cardActivity.__typename) {
        case 'CardActivityTransactionEvent':
          return {
            transactionEventId: cardActivity.transactionEventId,
          } as const satisfies CardActivityFeedCursorInput
        case 'CardActivityCardPayment':
          return {
            achTransferId: cardActivity.achTransferId,
          } as const satisfies CardActivityFeedCursorInput
        case 'CardActivityStatementCredit':
          return {
            statementCreditId: cardActivity.statementCreditId,
          } as const satisfies CardActivityFeedCursorInput
        case 'CardActivityFinancialEvent':
          return {
            // @ts-expect-error @ts-ignore
            financialEventId: cardActivity.financialEventId,
          } as const satisfies CardActivityFeedCursorInput
        default:
          // if this check fails, then we haven't checked all `__typename`s
          true satisfies NonNullable<typeof cardActivity> extends never ? true : false
          logError('Unknown card activity type for useGetCardActivityCursor', cardActivity)
          return undefined
      }
    },
    [logError],
  )
}
